import debugx from "debugx";

const debug = debugx("bikedash:camera");

// Bluetooth Web API type extensions
declare global {
  interface Navigator {
    bluetooth?: {
      requestDevice(options?: RequestDeviceOptions): Promise<BluetoothDevice>;
    };
  }

  interface RequestDeviceOptions {
    filters?: BluetoothLEScanFilter[];
    optionalServices?: BluetoothServiceUUID[];
  }

  interface BluetoothLEScanFilter {
    services?: BluetoothServiceUUID[];
    name?: string;
    namePrefix?: string;
  }

  type BluetoothServiceUUID = string | number;

  interface BluetoothDevice extends EventTarget {
    id: string;
    name?: string;
    gatt?: BluetoothRemoteGATTServer;
    watchAdvertisements(): Promise<void>;
    unwatchAdvertisements(): void;
  }

  interface BluetoothRemoteGATTServer {
    device: BluetoothDevice;
    connected: boolean;
    connect(): Promise<BluetoothRemoteGATTServer>;
    disconnect(): void;
    getPrimaryService(
      service: BluetoothServiceUUID,
    ): Promise<BluetoothRemoteGATTService>;
    getPrimaryServices(
      service?: BluetoothServiceUUID,
    ): Promise<BluetoothRemoteGATTService[]>;
  }

  interface BluetoothRemoteGATTService {
    device: BluetoothDevice;
    uuid: string;
    isPrimary: boolean;
    getCharacteristic(
      characteristic: BluetoothServiceUUID,
    ): Promise<BluetoothRemoteGATTCharacteristic>;
    getCharacteristics(
      characteristic?: BluetoothServiceUUID,
    ): Promise<BluetoothRemoteGATTCharacteristic[]>;
  }

  interface BluetoothRemoteGATTCharacteristic extends EventTarget {
    service: BluetoothRemoteGATTService;
    uuid: string;
    properties: BluetoothCharacteristicProperties;
    value?: DataView;
    readValue(): Promise<DataView>;
    writeValue(value: BufferSource): Promise<void>;
    writeValueWithResponse(value: BufferSource): Promise<void>;
    writeValueWithoutResponse(value: BufferSource): Promise<void>;
    startNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
    stopNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
  }

  interface BluetoothCharacteristicProperties {
    broadcast: boolean;
    read: boolean;
    writeWithoutResponse: boolean;
    write: boolean;
    notify: boolean;
    indicate: boolean;
    authenticatedSignedWrites: boolean;
    reliableWrite: boolean;
    writableAuxiliaries: boolean;
  }
}

export type CameraType = "bluetooth" | "phone";
export type CameraQuality = "High" | "Medium" | "Low" | "Blocky" | "Trash";
export type CameraResolution =
  | "QQVGA"
  | "QVGA"
  | "HQVGA"
  | "CIF"
  | "VGA"
  | "AUTO";
export type CameraFacingMode = "user" | "environment";
export type CameraLayout = "regular" | "ultrawide" | "full" | "full-wide";

interface CameraSettings {
  type: CameraType;
  quality: CameraQuality;
  resolution: CameraResolution;
  enabled: boolean; // Global enable - shows camera button in header
  active: boolean; // Currently active - not in OFF mode
  facingMode: CameraFacingMode;
  layout: CameraLayout;
}

interface BluetoothChunk {
  type: number;
  chunk: number;
  index: number;
  length: number;
  payload: Uint8Array;
}

const SERVICE_UUID = "1035f6e6-1531-41a1-a5e1-19b23b67656d";
const CHARACTERISTIC_TX_UUID = "eb4136f5-2914-420d-b92b-7639149a4be4";
const CHARACTERISTIC_RX_UUID = "5b009df0-2663-435e-af40-01ba288adef8";

let CHUNK_SIZE = 450;

export class CameraService {
  private static instance: CameraService;
  private canvas: HTMLCanvasElement | null = null; // For bluetooth camera only
  private videoElement: HTMLVideoElement | null = null; // For phone camera display
  private settings: CameraSettings;
  private subscribers: Set<(frame: ImageBitmap | null) => void> = new Set();
  private logSubscribers: Set<(message: string) => void> = new Set();

  // Error state
  private lastError: string | null = null;

  // Bluetooth state
  private btDevice: BluetoothDevice | null = null;
  private btServer: BluetoothRemoteGATTServer | null = null;
  private btTxChar: BluetoothRemoteGATTCharacteristic | null = null;
  private btRxChar: BluetoothRemoteGATTCharacteristic | null = null;
  private btConnected = false;
  private btCommandQueue: string[] = [];

  // Photo assembly state
  private photoLength = 0;
  private photoParts: { [key: number]: Uint8Array } = {};
  private lastChunkTime = 0;

  // Phone camera state
  private mediaStream: MediaStream | null = null;

  private constructor() {
    this.settings = this.loadSettings();
    this.startChunkWatchdog();
    this.startCommandQueueProcessor();
  }

  static getInstance(): CameraService {
    if (!CameraService.instance) {
      CameraService.instance = new CameraService();
    }
    return CameraService.instance;
  }

  private loadSettings(): CameraSettings {
    const stored = localStorage.getItem("cameraSettings");
    if (stored) {
      const settings = JSON.parse(stored);
      // Add default layout if not present (for backwards compatibility)
      if (!settings.layout) {
        settings.layout = "regular";
      }
      // Don't persist active state - always start as inactive
      settings.active = false;
      return settings;
    }
    return {
      type: "phone",
      quality: "Medium",
      resolution: "HQVGA",
      enabled: false,
      active: false,
      facingMode: "environment",
      layout: "regular",
    };
  }

  private saveSettings() {
    localStorage.setItem("cameraSettings", JSON.stringify(this.settings));
  }

  setCanvas(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    debug("Canvas set for camera service (bluetooth)");
  }

  setVideoElement(video: HTMLVideoElement) {
    this.videoElement = video;
    debug("Video element set for camera service (phone)");
  }

  getSettings(): CameraSettings {
    return { ...this.settings };
  }

  async updateSettings(updates: Partial<CameraSettings>) {
    const oldType = this.settings.type;
    const oldEnabled = this.settings.enabled;
    const oldFacingMode = this.settings.facingMode;

    this.settings = { ...this.settings, ...updates };
    this.saveSettings();

    // Handle type change
    if (updates.type && updates.type !== oldType) {
      const wasActive = this.settings.active;

      debug(
        "Camera type changing: %s -> %s (wasActive=%s)",
        oldType,
        updates.type,
        wasActive,
      );

      // Stop the old camera completely
      if (oldType === "phone" && this.mediaStream) {
        this.stopPhoneCamera();
        this.settings.active = false;
        debug("Stopped phone camera, set active=false");
      } else if (oldType === "bluetooth") {
        // Stop BT stream (but don't disconnect)
        this.photoLength = 0;
        this.photoParts = {};
        this.settings.active = false;
        debug(
          "Stopped BT stream (cleared photo state), set active=false (btConnected=%s)",
          this.btConnected,
        );
      }

      // The new camera will be started by the start() call in handleCameraSelect
      // We don't start it here because we want the UI flow to control that
      debug(
        "Camera type switched from %s to %s, active is now %s",
        oldType,
        updates.type,
        this.settings.active,
      );
    }

    // Handle facingMode change for phone camera (only if already running)
    if (
      updates.facingMode &&
      updates.facingMode !== oldFacingMode &&
      this.settings.type === "phone" &&
      this.mediaStream &&
      this.settings.enabled
    ) {
      this.stopPhoneCamera();
      await this.startPhoneCamera();
    }

    // Don't auto-start camera when enabled changes - user must click button
    // Only stop if being disabled
    if (updates.enabled === false && oldEnabled === true) {
      await this.stop();
    }

    // If active state is being changed via settings update
    if (
      updates.active !== undefined &&
      updates.active !== this.settings.active
    ) {
      if (updates.active === false) {
        // Stop camera when set to inactive
        await this.stop();
      }
    }

    // Update BT settings if connected
    if (this.btConnected) {
      if (updates.resolution) {
        const frameX = this.getResolutionValue(updates.resolution);
        if (frameX) {
          this.queueCommand(`size ${frameX}`);
        }
      }
      if (updates.quality) {
        const frameQ = this.getQualityValue(updates.quality);
        if (frameQ) {
          this.queueCommand(`quality ${frameQ}`);
        }
      }
    }

    debug("Camera settings updated: %o", this.settings);
  }

  private getResolutionValue(resolution: CameraResolution): number | null {
    const map: { [key: string]: number } = {
      QQVGA: 160,
      QVGA: 320,
      HQVGA: 240,
      CIF: 400,
      VGA: 640,
    };
    return map[resolution] || null;
  }

  private getQualityValue(quality: CameraQuality): number | null {
    const map: { [key: string]: number } = {
      High: 4,
      Medium: 12,
      Low: 18,
      Blocky: 30,
      Trash: 50,
    };
    return map[quality] || null;
  }

  async start() {
    if (!this.settings.enabled) {
      debug("Start called but camera not enabled");
      return;
    }

    // Mark camera as active
    this.settings.active = true;
    this.saveSettings();

    if (this.settings.type === "bluetooth") {
      if (!this.btConnected) {
        await this.connectBluetooth();
      }
      if (this.btConnected) {
        // Send resolution and quality settings before starting stream
        const frameX = this.getResolutionValue(this.settings.resolution);
        if (frameX) {
          this.queueCommand(`size ${frameX}`);
        }
        const frameQ = this.getQualityValue(this.settings.quality);
        if (frameQ) {
          this.queueCommand(`quality ${frameQ}`);
        }
        debug(
          "Starting BT camera stream (resolution=%s, quality=%s)",
          this.settings.resolution,
          this.settings.quality,
        );

        // Wait for size/quality commands to be sent before requesting photo
        // Queue processor runs every 100ms, so wait 500ms to ensure they're sent
        setTimeout(() => {
          if (
            this.btConnected &&
            this.settings.active &&
            this.settings.type === "bluetooth"
          ) {
            this.sendCommand("photo");
          }
        }, 500);
      }
    } else if (this.settings.type === "phone") {
      // Don't restart if already running
      if (this.mediaStream && this.videoElement) {
        debug("Phone camera already running");
        return;
      }
      await this.startPhoneCamera();
    }
  }

  async stop() {
    // Mark camera as inactive
    this.settings.active = false;
    this.saveSettings();

    if (this.settings.type === "bluetooth" && this.btConnected) {
      // Don't disconnect, just stop requesting photos
      this.photoLength = 0;
      this.photoParts = {};
    } else if (this.settings.type === "phone") {
      this.stopPhoneCamera();
    }
    this.notifySubscribers(null);
  }

  async toggle() {
    this.settings.enabled = !this.settings.enabled;
    this.saveSettings();
    debug("Camera toggled - enabled: %s", this.settings.enabled);
    if (this.settings.enabled) {
      debug("Starting camera (type: %s)", this.settings.type);
      await this.start();
    } else {
      debug("Stopping camera");
      await this.stop();
    }
  }

  // Bluetooth methods
  async connectBluetooth() {
    try {
      this.lastError = null; // Clear previous errors

      if (!navigator.bluetooth) {
        throw new Error("Bluetooth not supported in this browser");
      }

      debug("Requesting Bluetooth device...");
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [SERVICE_UUID] }],
      });

      const server = await device.gatt!.connect();
      const service = await server.getPrimaryService(SERVICE_UUID);
      const txChar = await service.getCharacteristic(CHARACTERISTIC_TX_UUID);
      const rxChar = await service.getCharacteristic(CHARACTERISTIC_RX_UUID);

      rxChar.addEventListener(
        "characteristicvaluechanged",
        this.handleBtNotification.bind(this),
      );
      await rxChar.startNotifications();

      this.btDevice = device;
      this.btServer = server;
      this.btTxChar = txChar;
      this.btRxChar = rxChar;
      this.btConnected = true;

      // Send initial settings
      const frameX = this.getResolutionValue(this.settings.resolution);
      if (frameX) {
        this.queueCommand(`size ${frameX}`);
      }
      const frameQ = this.getQualityValue(this.settings.quality);
      if (frameQ) {
        this.queueCommand(`quality ${frameQ}`);
      }

      debug("Bluetooth connected successfully");
      this.notifyLogSubscribers("✓ Bluetooth connected");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.lastError = errorMessage;
      debug.error("Bluetooth connection error: %o", error);
      this.notifyLogSubscribers(`✗ BT error: ${errorMessage}`);
      // Don't throw - just store the error
    }
  }

  async disconnectBluetooth() {
    try {
      if (this.btDevice?.gatt?.connected) {
        this.btDevice.gatt.disconnect();
      }
    } catch (e) {
      debug.error("Error disconnecting BT: %o", e);
    }

    this.btDevice = null;
    this.btServer = null;
    this.btTxChar = null;
    this.btRxChar = null;
    this.btConnected = false;
    this.photoLength = 0;
    this.photoParts = {};

    debug("Bluetooth disconnected");
    this.notifyLogSubscribers("Bluetooth disconnected");
  }

  private async sendCommand(command: string): Promise<Error | void> {
    try {
      if (!this.btTxChar) return;
      const enc = new TextEncoder();
      const data = enc.encode(command);
      await this.btTxChar.writeValue(data);
      debug("BT command sent: %s", command);
    } catch (error) {
      debug.error("Send BT command error: %o", error);
      if (
        error instanceof Error &&
        error.toString().includes("GATT operation already in progress")
      ) {
        return error;
      }
      this.disconnectBluetooth();
      throw error;
    }
  }

  private queueCommand(command: string) {
    this.btCommandQueue.push(command);
    debug("BT command queued: %s", command);
  }

  private startCommandQueueProcessor() {
    setInterval(() => {
      if (this.btCommandQueue.length > 0 && this.btTxChar && this.btConnected) {
        const command = this.btCommandQueue.shift()!;
        this.sendCommand(command).catch((error) => {
          if (
            error?.toString()?.includes("GATT operation already in progress")
          ) {
            this.btCommandQueue.unshift(command); // Re-queue
          }
        });
      }
    }, 100);
  }

  private handleBtNotification(event: Event) {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    const value = target.value!;
    const dataView = new DataView(value.buffer);

    const type = dataView.getUint8(0);
    const chunk = dataView.getUint16(2);
    const index = dataView.getUint16(4);
    const length = dataView.getUint16(6);
    const payload = new Uint8Array(value.buffer, 8, length);

    this.lastChunkTime = Date.now();

    if (type === 1) {
      // Log message
      const message = new TextDecoder().decode(payload);
      debug("BT log: %s", message);
      this.notifyLogSubscribers(message);
    } else if (type === 2) {
      // Error message
      const message = new TextDecoder().decode(payload);
      debug.error("BT error: %s", message);
      this.notifyLogSubscribers(`ERROR: ${message}`);
    } else if (type === 3) {
      // Photo metadata
      CHUNK_SIZE = chunk || 450;
      this.photoLength = index;
      this.photoParts = {};
      debug("Photo length announced: %d (chunk size: %d)", index, CHUNK_SIZE);
      this.requestMissingChunks();
    } else if (type === 4) {
      // Photo chunk
      this.photoParts[index / CHUNK_SIZE] = payload;
      debug("Received chunk #%d (length=%d)", index / CHUNK_SIZE, length, {
        throttle: "auto",
      });
    } else if (type === 5) {
      // End of stream
      this.requestMissingChunks();
    }
  }

  private requestMissingChunks() {
    const totalLen = this.photoLength;
    if (!totalLen) return;

    const totalParts = Math.ceil(totalLen / CHUNK_SIZE);
    const missingIndices: number[] = [];

    for (let i = 0; i < totalParts; i++) {
      if (!this.photoParts[i]) {
        missingIndices.push(i);
        if (missingIndices.length >= 25) break;
      }
    }

    if (missingIndices.length > 0) {
      const command = "photo " + missingIndices.join(" ");
      debug("Requesting missing parts: %s", command);
      this.sendCommand(command);
    } else {
      // Assemble photo
      this.assemblePhoto();
    }
  }

  private async assemblePhoto() {
    const totalLen = this.photoLength;
    const totalParts = Math.ceil(totalLen / CHUNK_SIZE);
    const fullPhoto = new Uint8Array(totalLen);

    for (let i = 0; i < totalParts; i++) {
      fullPhoto.set(this.photoParts[i], i * CHUNK_SIZE);
    }

    try {
      const bitmap = await createImageBitmap(
        new Blob([fullPhoto], { type: "image/jpeg" }),
      );

      if (this.canvas) {
        const ctx = this.canvas.getContext("2d");
        if (ctx) {
          this.canvas.width = bitmap.width;
          this.canvas.height = bitmap.height;
          ctx.drawImage(bitmap, 0, 0);
        }
      }

      this.notifySubscribers(bitmap);

      // Clear for next photo
      this.photoLength = 0;
      this.photoParts = {};

      // Request next photo if still enabled AND active AND using bluetooth camera
      const shouldRequestNext =
        this.btConnected &&
        this.settings.enabled &&
        this.settings.active &&
        this.settings.type === "bluetooth";

      debug(
        "Photo assembled - shouldRequestNext=%s (btConnected=%s, enabled=%s, active=%s, type=%s)",
        shouldRequestNext,
        this.btConnected,
        this.settings.enabled,
        this.settings.active,
        this.settings.type,
      );

      if (shouldRequestNext) {
        await this.sendCommand("photo");
      }
    } catch (error) {
      debug.error("Error creating image bitmap: %o", error);
    }
  }

  private startChunkWatchdog() {
    setInterval(() => {
      const shouldWatch =
        this.btConnected &&
        this.settings.enabled &&
        this.settings.active &&
        this.settings.type === "bluetooth";

      if (!shouldWatch) {
        debug(
          "Watchdog: NOT watching (btConnected=%s, enabled=%s, active=%s, type=%s)",
          this.btConnected,
          this.settings.enabled,
          this.settings.active,
          this.settings.type,
          { throttle: "auto" },
        );
        return;
      }

      const now = Date.now();
      if (now - this.lastChunkTime > 1500 && this.photoLength === 0) {
        // Reset and request new photo
        this.photoParts = {};
        this.sendCommand("photo");
        debug("Watchdog: Requesting new photo");
      }
    }, 1000);
  }

  // Phone camera methods
  private async startPhoneCamera() {
    try {
      debug(
        "Starting phone camera with facingMode: %s",
        this.settings.facingMode,
      );

      // Stop any existing camera first to avoid conflicts
      if (this.mediaStream) {
        debug("Stopping previous phone camera before starting new one");
        this.stopPhoneCamera();
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("getUserMedia not supported in this browser");
      }

      // iOS-friendly constraints - keep it simple, iOS is picky
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: this.settings.facingMode,
        },
        audio: false,
      };

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Set the stream on the video element if it's been set
      if (this.videoElement) {
        this.videoElement.srcObject = this.mediaStream;

        // Wait for video to be ready
        await new Promise<void>((resolve) => {
          this.videoElement!.addEventListener(
            "loadedmetadata",
            () => {
              debug(
                "Video metadata loaded - dimensions: %dx%d",
                this.videoElement!.videoWidth,
                this.videoElement!.videoHeight,
              );
              resolve();
            },
            { once: true },
          );
        });

        await this.videoElement.play();
        debug("Video playing");
      } else {
        debug.warn(
          "Video element not set - camera stream acquired but not displayed",
        );
      }

      const cameraName = this.settings.facingMode === "user" ? "front" : "rear";
      debug("Phone camera started (%s)", cameraName);
      this.notifyLogSubscribers(`✓ Phone camera started (${cameraName})`);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const errorName = error instanceof DOMException ? error.name : "";

      debug.error("Phone camera error: %o", error);

      // Provide helpful error messages
      if (
        errorName === "NotFoundError" ||
        errorMessage.includes("Requested device not found")
      ) {
        this.notifyLogSubscribers(
          `✗ No camera found (simulator or no camera available)`,
        );
        this.lastError = "No camera available";
      } else if (
        errorName === "NotAllowedError" ||
        errorMessage.includes("Permission denied")
      ) {
        this.notifyLogSubscribers(`✗ Camera permission denied`);
        this.lastError = "Camera permission denied";
      } else if (
        errorMessage.includes("constraint") ||
        errorName === "OverconstrainedError"
      ) {
        this.notifyLogSubscribers(`✗ Camera constraint not supported`);
        this.lastError = "Camera constraint not supported";
      } else {
        this.notifyLogSubscribers(`✗ Camera error: ${errorMessage}`);
        this.lastError = errorMessage;
      }

      // Don't throw - just set error state so UI can handle it gracefully
      this.settings.active = false;
      this.saveSettings();
    }
  }

  private stopPhoneCamera() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    debug("Phone camera stopped");
    this.notifyLogSubscribers("Phone camera stopped");
  }

  // Subscriber methods
  subscribe(callback: (frame: ImageBitmap | null) => void) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  subscribeToLogs(callback: (message: string) => void) {
    this.logSubscribers.add(callback);
    return () => this.logSubscribers.delete(callback);
  }

  private notifySubscribers(frame: ImageBitmap | null) {
    this.subscribers.forEach((callback) => callback(frame));
  }

  private notifyLogSubscribers(message: string) {
    this.logSubscribers.forEach((callback) => callback(message));
  }

  isConnected(): boolean {
    return (
      (this.settings.type === "bluetooth" && this.btConnected) ||
      (this.settings.type === "phone" && this.mediaStream !== null)
    );
  }

  isBluetoothConnected(): boolean {
    return this.btConnected;
  }

  isEnabled(): boolean {
    return this.settings.enabled;
  }

  isActive(): boolean {
    return this.settings.active;
  }

  isRunning(): boolean {
    return (
      (this.settings.type === "bluetooth" &&
        this.btConnected &&
        this.settings.active) ||
      (this.settings.type === "phone" &&
        this.mediaStream !== null &&
        this.settings.active)
    );
  }

  getLastError(): string | null {
    return this.lastError;
  }

  clearError(): void {
    this.lastError = null;
  }

  async destroy() {
    await this.stop();
    if (this.btConnected) {
      await this.disconnectBluetooth();
    }
    this.subscribers.clear();
    this.logSubscribers.clear();
  }
}

export const getCameraService = () => CameraService.getInstance();
